import { Alert, Box, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useMemo, useState } from 'react';
import { IEditorMessages } from '../../../../types';
import { InactiveStoryMergeTagsType, useMergeTagsContext } from '../../../common/context/MergeTagsContext';

import { INACTIVE_MERGE_TAGS_BANNER_CLASS as ROOT } from './InactiveStoryMergeTagsBanner.styles';

const MAX_NUMBER_INITIALLY_VISIBLE = 2;

export const getTypeProps = (type: InactiveStoryMergeTagsType, messages: IEditorMessages) => {
  switch (type) {
    case InactiveStoryMergeTagsType.BudgetNotFound:
      return {
        header: messages.inactiveMergeTagsBudget,
        subheader: messages.inactiveMergeTagsBudgetDesc,
        noList: true
      };
    case InactiveStoryMergeTagsType.FieldsNotFound:
      return { header: messages.inactiveMergeTagsFields };
    default:
      return { header: messages.inactiveMergeTags };
  }
};

export type InactiveStoryMergeTagsBannerProps = {
  document: Document;
  messages: IEditorMessages;
  type?: InactiveStoryMergeTagsType;
  onRemoveAllClick(): Promise<void>;
};

export const InactiveStoryMergeTagsBanner = ({
  document,
  messages,
  type,
  onRemoveAllClick
}: InactiveStoryMergeTagsBannerProps) => {
  const { storyMergeTagList, loadingMergeTags } = useMergeTagsContext();
  const [showBanner, setShowBanner] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const inactiveStoryMergeTags = useMemo(
    () => storyMergeTagList.filter((storyMergeTag) => storyMergeTag.inactive),
    [storyMergeTagList]
  );
  const visibleInactiveStoryMergeTags = useMemo(
    () =>
      inactiveStoryMergeTags.length > MAX_NUMBER_INITIALLY_VISIBLE && !showAll
        ? inactiveStoryMergeTags.slice(0, MAX_NUMBER_INITIALLY_VISIBLE)
        : inactiveStoryMergeTags,
    [inactiveStoryMergeTags, showAll]
  );
  const typeProps = useMemo(() => getTypeProps(type, messages), [type, messages]);

  const onLinkClick = (mergeTagHtmlId: string) => {
    const mergeTagElement = document.getElementById(mergeTagHtmlId);
    if (mergeTagElement) mergeTagElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loadingMergeTags || !inactiveStoryMergeTags?.length || !showBanner) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      className={ROOT}
      data-test="inactive-story-merge-tags-banner"
      onClose={() => setShowBanner(false)}
    >
      <Typography
        component="div"
        className={`${ROOT}__header`}
        data-test="inactive-story-merge-tags-banner-header"
      >
        {typeProps.header}
      </Typography>
      <Box data-test="inactive-story-merge-tags-banner-subheader">
        {typeProps.subheader || (
          <>
            <Box
              component="button"
              type="button"
              data-test="inactive-story-merge-tags-banner-remove-all"
              className={`${ROOT}__link`}
              onClick={onRemoveAllClick}
            >
              {messages.removeAllInactiveMergeTags}
            </Box>{' '}
            {messages.inactiveMergeTagsDesc}:
          </>
        )}
      </Box>
      {!typeProps.noList && (
        <>
          <Box component="ol" data-test="inactive-story-merge-tags-banner-list">
            {visibleInactiveStoryMergeTags.map(({ htmlId, value }) => (
              <li key={htmlId}>
                <Box
                  component="button"
                  type="button"
                  data-test="inactive-story-merge-tag-link"
                  className={`${ROOT}__link ${ROOT}__tag-link`}
                  title={value}
                  onClick={() => onLinkClick(htmlId)}
                >
                  {`{`}
                  <Box component="span" className="ellipsis">{value}</Box>
                  {`}`}
                </Box>
              </li>
            ))}
          </Box>
          {inactiveStoryMergeTags.length > MAX_NUMBER_INITIALLY_VISIBLE && (
            <Box className={`${ROOT}__show-more-container`}>
              <Box
                component="button"
                type="button"
                className={`${ROOT}__show-more`}
                data-test="inactive-story-merge-tags-banner-show-more"
                aria-expanded={showAll}
                onClick={() => setShowAll((prev) => !prev)}
              >
                {showAll ? (
                  <ExpandLessIcon fontSize="small" aria-hidden="true" />
                ) : (
                  <ExpandMoreIcon fontSize="small" aria-hidden="true" />
                )}
                {showAll ? messages.showLess : messages.showMore}
              </Box>
            </Box>
          )}
        </>
      )}
    </Alert>
  );
};
